<template>
  <div class="tw-inline-block" ref="root">
    <button
      type="button"
      class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-600 hover:tw-bg-white/80 hover:tw-text-slate-900"
      aria-label="Actions"
      @click.stop="toggle"
    >
      <font-awesome-icon icon="ellipsis-vertical" class="tw-h-4 tw-w-4" />
    </button>

    <teleport to="body">
      <div
        v-if="isOpen"
        class="tw-fixed tw-inset-0 tw-z-[9999]"
        @click="close"
      >
        <div
          ref="menuEl"
          class="tw-absolute tw-min-w-[220px] tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-py-1 tw-shadow-lg"
          :style="menuStyle"
          @click.stop
        >
          <ul class="tw-m-0 tw-list-none tw-p-0">
            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="emitAndClose('edit')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="pen-to-square" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Edit Trip</span>
                </span>
              </button>
            </li>

            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="emitAndClose('addBefore')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="arrow-up" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Add Trip Before</span>
                </span>
              </button>
            </li>

            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="emitAndClose('addAfter')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="arrow-down" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Add Trip After</span>
                </span>
              </button>
            </li>

            <li v-if="showStartTrip" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-emerald-700 hover:tw-text-emerald-800"
                :disabled="isActionPending"
                @click="emitAndClose('startTrip')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <div v-if="isActionPending" uk-spinner></div>
                  <font-awesome-icon v-else icon="play" class="tw-h-4 tw-w-4 tw-text-emerald-700" />
                  <span>{{ isActionPending ? 'Starting...' : 'Start Trip' }}</span>
                </span>
              </button>
            </li>

            <li v-if="showCompleteTrip" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-rose-700 hover:tw-text-rose-800"
                :disabled="isActionPending"
                @click="emitAndClose('completeTrip')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <div v-if="isActionPending" uk-spinner></div>
                  <font-awesome-icon v-else icon="circle-check" class="tw-h-4 tw-w-4 tw-text-rose-700" />
                  <span>{{ isActionPending ? 'Completing...' : 'Complete Trip' }}</span>
                </span>
              </button>
            </li>

            <li v-if="showRevertTrip" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="emitAndClose('revertTrip')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="rotate-left" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Revert Trip</span>
                </span>
              </button>
            </li>

            <li v-if="showCancelTrip" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="emitAndClose('cancelTrip')"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="ban" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Cancel Trip</span>
                </span>
              </button>
            </li>
            
            <!-- disabling this, as it is not really used until the Dispatch Link can be used by drivers actually -->
            <!-- 
            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="copyDispatchInfo"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="clipboard" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Copy Dispatch Info</span>
                </span>
              </button>
            </li> -->

            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <a
                :href="printDispatchHref"
                target="_blank"
                class="tw-block tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600 tw-no-underline hover:tw-no-underline"
                @click="close"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="print" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Print Dispatch Sheet</span>
                </span>
              </a>
            </li>

            <!-- disable for now: not anywhere close to getting this feature wired -->
            <!-- <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600"
                @click="sendDispatchEmail"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="envelope" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Email Dispatch to Driver</span>
                </span>
              </button>
            </li> -->

            <li v-if="!isEditing" class="tw-px-3 tw-py-1">
              <a
                :href="printBLHref"
                target="_blank"
                class="tw-block tw-w-full tw-text-left tw-text-sm tw-text-slate-700 hover:tw-text-blue-600 tw-no-underline hover:tw-no-underline"
                @click="close"
              >
                <span class="tw-flex tw-items-center tw-gap-2">
                  <font-awesome-icon icon="file-lines" class="tw-h-4 tw-w-4 tw-text-slate-500" />
                  <span>Print BL</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'; 
import SendDispatchEmailMutation from '@/gql/orders/trips/mutations/SendDispatchEmail.gql';
import { pushToast } from '@/lib/toast';

export default defineComponent({
  props: {
    trip: {
      type: Object,
      required: true,
    },
    containerId: {
      type: [Number, String],
      required: true,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    TRIP_STATUS: {
      type: Object,
      required: true,
    },
    canStartTrip: {
      type: Boolean,
      default: true,
    },
    canCompleteTrip: {
      type: Boolean,
      default: true,
    },
    isActionPending: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['edit', 'addBefore', 'addAfter', 'startTrip', 'completeTrip', 'revertTrip', 'cancelTrip', 'save', 'cancelEdit'],
  data() {
    return {
      isOpen: false,
      menuX: 0,
      menuY: 0,
    };
  },
  computed: {
    menuStyle(): Record<string, string> {
      const vm = (this as any);
      return {
        left: `${vm.menuX}px`,
        top: `${vm.menuY}px`,
      };
    },
    printDispatchHref(): string {
      const trip: any = (this as any).trip;
      return `/print/order/dispatch/${(this as any).orderId}/container/${(this as any).containerId}/trip/${trip.id}`;
    },
    printBLHref(): string {
      return `/print/order/bl/${(this as any).orderId}/container/${(this as any).containerId}`;
    },
    showStartTrip(): boolean {
      const vm = (this as any);
      const trip: any = vm.trip;
      return vm.canStartTrip && !vm.isEditing && trip.status != 'In Progress' && trip.status != 'Complete';
    },
    showCompleteTrip(): boolean {
      const vm = (this as any);
      const trip: any = vm.trip;
      return vm.canCompleteTrip && !vm.isEditing && trip.status == 'In Progress';
    },
    showRevertTrip(): boolean {
      const vm = (this as any);
      const trip: any = vm.trip;
      return !vm.isEditing && (trip.status == vm.TRIP_STATUS.IN_PROGRESS || trip.status === vm.TRIP_STATUS.COMPLETE);
    },
    showCancelTrip(): boolean {
      const vm = (this as any);
      const trip: any = vm.trip;
      return !vm.isEditing && (trip.status == vm.TRIP_STATUS.NOT_STARTED || !trip.status);
    },
  },
  mounted() {
    document.addEventListener('click', (this as any).onDocumentClick);
    window.addEventListener('scroll', (this as any).onWindowChange, true);
    window.addEventListener('resize', (this as any).onWindowChange);
    window.addEventListener('keydown', (this as any).onKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('click', (this as any).onDocumentClick);
    window.removeEventListener('scroll', (this as any).onWindowChange, true);
    window.removeEventListener('resize', (this as any).onWindowChange);
    window.removeEventListener('keydown', (this as any).onKeyDown);
  },
  methods: {
    toggle() {
      const vm = (this as any);
      vm.isOpen = !vm.isOpen;
      if (vm.isOpen) {
        vm.$nextTick(() => {
          vm.updateMenuPosition();
        });
      }
    },
    close() {
      (this as any).isOpen = false;
    },
    emitAndClose(evt: string) {
      (this as any).$emit(evt);
      (this as any).close();
    },
    onDocumentClick(e: MouseEvent) {
      const root = (this as any).$refs.root as HTMLElement | undefined;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      (this as any).close();
    },
    onWindowChange() {
      const vm = (this as any);
      if (!vm.isOpen) return;
      vm.updateMenuPosition();
    },
    onKeyDown(e: KeyboardEvent) {
      const vm = (this as any);
      if (!vm.isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        vm.close();
      }
    },
    updateMenuPosition() {
      const vm = (this as any);
      const root = vm.$refs.root as HTMLElement | undefined;
      if (!root) return;

      const menuEl = vm.$refs.menuEl as HTMLElement | undefined;

      const rect = root.getBoundingClientRect();
      const menuWidth = 220;
      const margin = 8;

      const menuHeight = menuEl?.offsetHeight || 280;

      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      let x = rect.right - menuWidth;
      let y = openUp ? rect.top - margin - menuHeight : rect.bottom + margin;

      x = Math.max(margin, Math.min(x, window.innerWidth - menuWidth - margin));
      y = Math.max(margin, Math.min(y, window.innerHeight - menuHeight - margin));

      vm.menuX = x;
      vm.menuY = y;
    },
    async sendDispatchEmail() {
      const vm = (this as any);
      const tripId = parseInt(vm.trip.id, 10);
      if (!vm.trip?.driver) {
        vm.close();
        pushToast('Cannot email dispatch: no driver is assigned to this trip.', 'error');
        return;
      }

      try {
        vm.close();
        
        const { data } = await vm.$apollo.mutate({
          mutation: SendDispatchEmailMutation,
          variables: {
            tripId: tripId,
          },
        });

        if (data.sendDispatchEmail.success) {
          pushToast('Dispatch sheet has been sent to the driver!', 'success')
        } else {
          pushToast('Dispatch email was not sent. Verify the driver email address and your mail configuration.', 'error');
        }
      } catch (error: any) {
        console.error(error);
        const gqlMessage = error?.graphQLErrors?.[0]?.message;
        const networkMessage = error?.networkError?.message;
        const message = gqlMessage || networkMessage || 'An error occurred while sending the dispatch email.';
        pushToast(message, 'error');
      }
    },

    addressName(addr: any): string {
      if (!addr) return '';
      return addr.shortName || addr.name || '';
    },
    formatScheduled(val: any): string {
      if (!val) return '';
      try {
        if (val instanceof Date) return val.toLocaleString();
        return String(val);
      } catch {
        return String(val);
      }
    },
    async copyDispatchInfo() {
      const vm = this as any;
      const trip: any = vm.trip || {};

      const orderId = String(vm.orderId ?? '');
      const containerId = String(vm.containerId ?? '');
      const tripId = String(trip.id ?? '');

      const scheduled = vm.formatScheduled(trip.scheduledDate);
      const origin = vm.addressName(trip.origin);
      const destination = vm.addressName(trip.destination);
      const driverName = trip?.driver?.fullName || trip?.driver?.name || '';
      const driverPhone = trip?.driver?.phone || trip?.driver?.user?.phone || '';

      const lines = [
        `Order #${orderId}`,
        `Container: ${containerId}`,
        `Trip: ${tripId}`,
        scheduled ? `Scheduled: ${scheduled}` : undefined,
        origin ? `Origin: ${origin}` : undefined,
        destination ? `Destination: ${destination}` : undefined,
        driverName ? `Driver: ${driverName}${driverPhone ? ` (${driverPhone})` : ''}` : undefined,
        `Dispatch sheet: ${window.location.origin}${vm.printDispatchHref}`,
      ].filter(Boolean) as string[];

      const text = lines.join('\n');
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        pushToast('Dispatch info copied to clipboard.', 'success');
      } catch (_e) {
        pushToast('Failed to copy dispatch info to clipboard.', 'error');
      } finally {
        vm.close();
      }
    },
  },
});
</script>
